import { useEffect, useRef, useState } from 'react';
import api from '../../services/api';

/**
 * reCAPTCHA v2 checkbox — site key from RECAPTCHA_SITE_KEY on server.
 * Mounts once per page visit; parent re-renders (typing in email, etc.) must not
 * recreate the widget or Google throws and React can white-screen the login form.
 */
export default function RecaptchaWidget({ onReady, disabled = false }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onReadyRef = useRef(onReady);
  const [siteKey, setSiteKey] = useState('');

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/auth/recaptcha-config')
      .then((res) => {
        if (!cancelled && res.data?.enabled && res.data.site_key) {
          setSiteKey(res.data.site_key);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;

    let cancelled = false;

    const publishGetter = () => {
      onReadyRef.current?.(() => {
        if (widgetIdRef.current === null || !window.grecaptcha?.getResponse) return '';
        return window.grecaptcha.getResponse(widgetIdRef.current);
      });
    };

    const mountWidget = () => {
      if (cancelled || !containerRef.current || widgetIdRef.current !== null) {
        publishGetter();
        return;
      }
      if (!window.grecaptcha?.render) return;

      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        theme: 'dark',
      });
      publishGetter();
    };

    const onScriptReady = () => {
      if (cancelled) return;
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(mountWidget);
      } else {
        mountWidget();
      }
    };

    if (window.grecaptcha?.render) {
      onScriptReady();
    } else {
      const existing = document.querySelector('script[data-propscope-recaptcha]');
      if (existing) {
        existing.addEventListener('load', onScriptReady, { once: true });
      } else {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.propscopeRecaptcha = '1';
        script.onload = onScriptReady;
        document.body.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
        try {
          window.grecaptcha.reset(widgetIdRef.current);
        } catch {
          /* widget may already be gone */
        }
      }
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  if (!siteKey) return null;

  return (
    <div
      ref={containerRef}
      className={disabled ? 'opacity-50 pointer-events-none' : 'flex justify-center'}
    />
  );
}
